import { useCallback, useState } from 'react';
import type { TaxIdType } from '@stripe/stripe-js';
import type { TaxIdValue } from '../components/StripeTaxIdElement';
import { getStripeTaxIdElementType } from '../utils/stripeTaxIdTypes';

const toTaxIdValue = (
  taxId?: string | null,
  taxType?: string | null
): TaxIdValue | null => (taxId && taxType ? { taxId, taxType } : null);

type UseStripeTaxIdFieldArgs = {
  taxId?: string | null;
  taxType?: string | null;
  enabled: boolean;
};

type UseStripeTaxIdFieldResult = {
  taxIdValue: TaxIdValue | null;
  defaultTaxId: string | undefined;
  defaultTaxIdType: TaxIdType | undefined;
  isBlocking: boolean;
  onChange: (value: TaxIdValue | null, complete: boolean) => void;
  reset: () => void;
};

/**
 * Wires Stripe's `TaxIdElement` to a form: holds the current value and
 * completeness, derives the element's prefill props from the saved values, and
 * reports whether an incomplete-but-non-empty entry should block submission.
 * `enabled` gates blocking so a hidden field never disables the form. `reset`
 * restores the saved values; consumers decide when to call it (e.g. on cancel
 * or when the saved values change).
 */
export default function useStripeTaxIdField({
  taxId,
  taxType,
  enabled,
}: UseStripeTaxIdFieldArgs): UseStripeTaxIdFieldResult {
  const [taxIdValue, setTaxIdValue] = useState<TaxIdValue | null>(() =>
    toTaxIdValue(taxId, taxType)
  );
  const [isComplete, setIsComplete] = useState(true);

  const onChange = useCallback(
    (value: TaxIdValue | null, complete: boolean) => {
      setTaxIdValue(value);
      setIsComplete(complete);
    },
    []
  );

  const reset = useCallback(() => {
    setTaxIdValue(toTaxIdValue(taxId, taxType));
    setIsComplete(true);
  }, [taxId, taxType]);

  return {
    taxIdValue,
    defaultTaxId: taxId ?? undefined,
    defaultTaxIdType: getStripeTaxIdElementType(taxType, taxId),
    isBlocking: enabled && !!taxIdValue && !isComplete,
    onChange,
    reset,
  };
}
